import React, { useState } from 'react';
import {
  Briefcase,
  MapPin,
  Check,
  X,
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Sparkles,
  Activity,
  Target,
  TrendingUp,
  Eye,
  Calendar,
  Send,
  Bookmark,
  GitBranch,
  ExternalLink,
} from 'lucide-react';
import { Checkbox } from 'src/components/ui/checkbox';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from 'src/components/ui/hover-card';
import { cn } from 'src/lib/utils';
import ScoreRing from 'src/components/common/ScoreRing';
import { tierColorVar, tintFrom, TIERS } from './matchData';

const SKILL_TAG_CLS = {
  matched:
    'bg-green-100 dark:bg-green-950/50 text-green-800 dark:text-green-400 border-green-200 dark:border-green-800',
  partial:
    'bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800',
  missing: 'bg-muted text-muted-foreground border-border',
};

const TIER_BADGE_CLS = {
  best: 'text-white',
  near: 'text-white',
  explore: 'bg-muted text-muted-foreground',
};

// Tier-specific expanded backgrounds
const DETAIL_BG_CLS = {
  best: 'bg-green-50/60 dark:bg-green-950/20',
  near: 'bg-amber-50/60 dark:bg-amber-950/20',
  explore: 'bg-muted/40 dark:bg-muted/20',
};

const TONE_TEXT = { warn: 'var(--ds-warning)', danger: 'var(--ds-danger)' };

const fitState = (passed) => {
  if (passed === true) return 'pass';
  if (passed === false) return 'fail';
  return 'warn';
};

const FitItem = ({ state, label }) => {
  const map = {
    pass: {
      color: 'var(--ds-success)',
      cls: 'text-muted-foreground',
      Icon: Check,
    },
    warn: {
      color: 'var(--ds-warning)',
      cls: 'text-ds-warning',
      Icon: AlertTriangle,
    },
    fail: { color: 'var(--ds-danger)', cls: 'text-ds-danger', Icon: X },
  };
  const { color, cls, Icon } = map[state] || map.pass;
  return (
    <span className={`inline-flex items-center gap-1 text-[11.5px] ${cls}`}>
      <Icon style={{ width: 11, height: 11, strokeWidth: 2.2, color }} />
      {label}
    </span>
  );
};

const Metric = ({ value, total, label, strong }) => (
  <div className="text-center min-w-[58px] px-3 relative">
    <div
      className="text-[16px] font-bold tabular-nums leading-[1.15] tracking-[-0.02em]"
      style={{
        color: strong ? 'var(--ds-success)' : 'var(--foreground-strong)',
      }}
    >
      {value}
      <span className="text-[11px] font-medium text-muted-foreground">
        /{total}
      </span>
    </div>
    <div className="text-[10px] font-medium uppercase tracking-[0.02em] mt-px text-muted-foreground">
      {label}
    </div>
  </div>
);

const ScoreBar = ({ label, value, color }) => {
  // Unavailable / null dimensions render as a 0 — empty bar, value 0.
  const pct = Math.round(value ?? 0);
  return (
    <div className="grid grid-cols-[104px_1fr_36px] items-center gap-2.5">
      <span className="text-[11.5px] font-medium truncate text-muted-foreground">
        {label}
      </span>
      <span className="h-2 rounded overflow-hidden bg-border">
        <span
          className="block h-full rounded"
          style={{
            width: `${pct}%`,
            background: color,
            transition: 'width .6s cubic-bezier(.4,0,.2,1)',
          }}
        />
      </span>
      <span className="font-mono text-[12.5px] font-bold tabular-nums text-right text-foreground-strong">
        {pct}
      </span>
    </div>
  );
};

const InfoRow = ({ k, v, tone }) => (
  <div className="flex justify-between items-center text-[12px]">
    <span className="font-medium text-muted-foreground">{k}</span>
    <span
      className="font-semibold tabular-nums text-right"
      style={{ color: TONE_TEXT[tone] || 'var(--foreground-strong)' }}
    >
      {v}
    </span>
  </div>
);

const SKILL_TAG_ICON = { matched: Check, missing: X };
const SkillTag = ({ name, status, req }) => {
  const Icon = SKILL_TAG_ICON[status] ?? null;
  const prefix = (() => {
    if (Icon)
      return <Icon style={{ width: 10, height: 10, strokeWidth: 2.5 }} />;
    if (status === 'partial') return '~';
    return null;
  })();
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 px-2.5 py-1 rounded-[5px] text-[11.5px] font-[550] border',
        SKILL_TAG_CLS[status] || SKILL_TAG_CLS.missing
      )}
    >
      {prefix}
      {name}
      {req && (
        <span className="text-[8.5px] font-bold tracking-[0.04em] opacity-75 ml-0.5">
          REQ
        </span>
      )}
    </span>
  );
};

// "IN_PROGRESS" → "In progress" — the fallback label for any status code
// the table below doesn't name yet.
const humanizeMappingStatus = (status) =>
  (status || '')
    .toLowerCase()
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

// Per-status metadata: {label, tone, active}. `active` — not the count —
// is what sorts an entry into "Still open" vs "Closed" and decides
// whether its status text gets colored or muted. Covers both flows this
// payload's `status` can come from (senior_level_flow's vocabulary, plus
// the reference design's own OFFER_RELEASED/INTERVIEW_SCHEDULED/etc.).
// Any status NOT in this table fails OPEN (tone amber, active: true) —
// showing a live process as closed is the expensive mistake here, not
// the reverse; see splitMappings' `drift` below for how that's surfaced.
const MAPPING_STATUS_META = {
  NEW: { label: 'Sourced', tone: 'brand', active: true },
  CONTACTED: { label: 'Contacted', tone: 'brand', active: true },
  ACKNOWLEDGED: { label: 'Acknowledged', tone: 'brand', active: true },
  READY_TO_SCHEDULE: {
    label: 'Ready to schedule',
    tone: 'amber',
    active: true,
  },
  INTERVIEW_SCHEDULED: {
    label: 'Interview booked',
    tone: 'amber',
    active: true,
  },
  IN_PROGRESS: { label: 'In progress', tone: 'amber', active: true },
  OFFER_RELEASED: { label: 'Offer released', tone: 'amber', active: true },
  ON_HOLD: { label: 'On hold', tone: 'muted', active: true },
  SHORTLISTED: { label: 'Shortlisted', tone: 'green', active: true },
  COMPLETED: { label: 'Completed', tone: 'green', active: true },
  FAILED: { label: 'Not progressed', tone: 'red', active: false },
  REJECTED: { label: 'Rejected', tone: 'red', active: false },
  NOT_INTERESTED: { label: 'Not interested', tone: 'muted', active: false },
  NO_SHOW: { label: 'No show', tone: 'muted', active: false },
  NON_RESPONSIVE: { label: 'Non responsive', tone: 'muted', active: false },
  WITHDRAWN: { label: 'Withdrew', tone: 'muted', active: false },
  DUPLICATE: { label: 'Duplicate', tone: 'muted', active: false },
};

// tone → this app's own ds-*/design-system vars, not the reference
// mockup's raw --amber/--green/--brand/--muted/--red custom properties.
const MAPPING_TONE_VARS = {
  amber: 'var(--ds-warning)',
  green: 'var(--ds-success)',
  brand: 'var(--primary)',
  red: 'var(--ds-danger)',
  muted: 'var(--muted-foreground)',
};

const mappingStatusMeta = (raw) =>
  MAPPING_STATUS_META[raw] || {
    label: humanizeMappingStatus(raw),
    tone: 'amber',
    active: true,
  };

// Splits one candidate's `other_requirement_mappings.requirements` into
// "still open" vs "closed" groups by status. `drift` flags the case where
// the API's own top-level `has_active_engagement` says something is live
// but nothing in the list parsed as active — i.e. the status table above
// is behind whatever the backend's real enum now includes. Surfaced in
// the footer note rather than silently swallowed either way.
const splitMappings = (mappings) => {
  const live = [];
  const done = [];
  (mappings.requirements || []).forEach((req) => {
    const meta = mappingStatusMeta(req.status);
    (meta.active ? live : done).push({ req, meta });
  });
  return {
    live,
    done,
    drift: !!mappings.has_active_engagement && live.length === 0,
  };
};

// One "still open"/"closed" popover row — a flat list item (no card
// border/fill, just a hover wash), matching the reference `.eng-item`
// exactly: dot · title · status+registration_id line · location/
// employment_type line · a trailing "go" chevron. registration_id is
// the human-readable reference a recruiter can quote to that
// requirement's owner — requirement_id stays purely for the
// /opportunity/:id route, since titles like "Credit Risk Manager"
// aren't unique.
const MappingEntry = ({ req, meta }) => (
  <a
    href={`/opportunity/${req.requirement_id}`}
    target="_blank"
    rel="noopener noreferrer"
    className="group flex gap-[9px] w-full text-left rounded-[7px] py-[7px] px-[6px] hover:bg-primary/5 transition-colors"
  >
    <span
      className="w-[7px] h-[7px] rounded-full mt-[5px] shrink-0"
      style={{ background: MAPPING_TONE_VARS[meta.tone] }}
    />
    <div className="flex-1 min-w-0">
      <div className="text-[12px] font-[600] text-foreground-strong leading-[1.35]">
        {req.title}
      </div>
      <div className="flex items-center gap-1.5 mt-[3px] text-[10.5px] text-muted-foreground">
        <span
          className="font-[600]"
          style={
            meta.active ? { color: MAPPING_TONE_VARS[meta.tone] } : undefined
          }
        >
          {meta.label}
        </span>
        {(req.registration_id || req.requirement_id) && (
          <>
            <span className="w-[3px] h-[3px] rounded-full bg-border shrink-0" />
            <span className="font-mono text-[10px] tracking-[-0.01em] text-muted-foreground">
              {req.registration_id || `#${req.requirement_id}`}
            </span>
          </>
        )}
      </div>
      {(req.location || req.employment_type) && (
        <div className="flex flex-wrap items-center gap-x-[11px] gap-y-1 mt-1 text-[10.5px] text-muted-foreground">
          {req.location && (
            <span className="inline-flex items-center gap-1 min-w-0">
              <MapPin
                style={{ width: 10.5, height: 10.5, strokeWidth: 1.8 }}
                className="shrink-0 text-muted-foreground/70"
              />
              {req.location}
            </span>
          )}
          {req.employment_type && (
            <span className="inline-flex items-center gap-1 min-w-0">
              <Briefcase
                style={{ width: 10.5, height: 10.5, strokeWidth: 1.8 }}
                className="shrink-0 text-muted-foreground/70"
              />
              {req.employment_type}
            </span>
          )}
        </div>
      )}
    </div>
    <ExternalLink
      style={{ width: 12, height: 12, strokeWidth: 2 }}
      className="shrink-0 self-start mt-0.5 text-muted-foreground/70 group-hover:text-primary transition-colors"
    />
  </a>
);

// Surfaces `other_requirement_mappings` — whether this candidate is already
// mapped to any OTHER requirement, so a recruiter doesn't submit/schedule
// them somewhere new without knowing they're already in play elsewhere.
// Entries are grouped "Still open" / "Closed" by status (splitMappings),
// which also drives the pill's own tone/count: `active` (amber pill) when
// anything is still open, or the drift case fires; `n` is the still-open
// count when active, else the raw total.
const OtherRequirementsPill = ({ name, mappings }) => {
  const { count } = mappings;
  if (!count) return null;

  const { live, done, drift } = splitMappings(mappings);
  const active = live.length > 0 || drift;
  const n = active ? live.length || count : count;

  // The verdict, not a caveat — matches the reference `.eng-note`: a flat
  // colored band (no border/card), 13/13/2 icon, rounded to close out the
  // popover's bottom corners.
  let footerNote;
  if (drift) {
    footerNote = (
      <div className="flex gap-2 items-start px-3.5 py-2.5 text-[11px] leading-[1.5] rounded-b-[11px] bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-400">
        <AlertTriangle
          style={{ width: 13, height: 13, strokeWidth: 2 }}
          className="shrink-0 mt-[1.5px]"
        />
        <span>
          Marked as having an open process, but none of the statuses above is
          recognized as open.{' '}
          <b className="font-[650]">
            Treat this as active and check the requirement directly.
          </b>
        </span>
      </div>
    );
  } else if (live.length > 0) {
    footerNote = (
      <div className="flex gap-2 items-start px-3.5 py-2.5 text-[11px] leading-[1.5] rounded-b-[11px] bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-400">
        <AlertTriangle
          style={{ width: 13, height: 13, strokeWidth: 2 }}
          className="shrink-0 mt-[1.5px]"
        />
        <span>
          Interviews may already be booked elsewhere.{' '}
          <b className="font-[650]">
            Confirm with the owning recruiter before you schedule.
          </b>
        </span>
      </div>
    );
  } else {
    footerNote = (
      <div className="flex gap-2 items-start px-3.5 py-2.5 text-[11px] leading-[1.5] rounded-b-[11px] bg-green-50 dark:bg-green-950/30 text-green-700 dark:text-green-400">
        <CheckCircle2
          style={{ width: 13, height: 13, strokeWidth: 2 }}
          className="shrink-0 mt-[1.5px]"
        />
        <span>
          Nothing open. <b className="font-[650]">Free to pursue.</b>
        </span>
      </div>
    );
  }

  // Hover-driven rather than click-driven — this sits inline in the row's
  // header, and a click would double as (or fight with) the row's own
  // click-to-expand. HoverCard (not Popover) is what makes this actually
  // reliable: it tracks the pointer continuously across trigger + content
  // itself, rather than each element independently firing its own mouse
  // enter/leave — an earlier version wired that manually on top of Popover
  // and it flickered shut the instant the cursor crossed the few-pixel gap
  // between the pill and the card, since that gap briefly left the pointer
  // over neither element. HoverCard's own openDelay/closeDelay exist to
  // absorb exactly that. Click still opens/closes it too (Radix's own
  // trigger behavior) as the touch-device fallback hover can't serve.
  return (
    <>
      {/* .eng-sep — everything left of this hairline grades the match;
          everything right of it describes the candidate's availability
          elsewhere. Also keeps an amber "active" pill from reading as
          part of an amber Near Match badge next to it. */}
      <span className="w-px h-3 bg-border shrink-0 mx-px" aria-hidden="true" />
      <HoverCard openDelay={150} closeDelay={150}>
        <HoverCardTrigger asChild>
          <button
            type="button"
            onClick={(e) => e.stopPropagation()}
            className={cn(
              'inline-flex items-center gap-[5px] h-5 px-[7px] rounded-[5px] border text-[10.5px] font-[600] tracking-[0.01em] whitespace-nowrap cursor-pointer transition-colors',
              active
                ? 'bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800 hover:bg-amber-100 dark:hover:bg-amber-950/50 hover:border-amber-300 dark:hover:border-amber-700'
                : 'bg-transparent text-muted-foreground border-border hover:bg-muted hover:border-muted-foreground/40 hover:text-foreground'
            )}
          >
            <GitBranch style={{ width: 11, height: 11, strokeWidth: 2 }} />
            <span className="font-mono font-[700]">{n}</span>
            <span>{active ? 'active' : 'past'}</span>
          </button>
        </HoverCardTrigger>
        <HoverCardContent
          align="start"
          className="w-[348px] p-0 overflow-hidden rounded-[12px]"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-[14px] pt-3 pb-[11px] border-b border-border">
            <div className="text-[12.5px] font-[650] text-foreground-strong tracking-[-0.01em]">
              Also in other requirements
            </div>
            <p className="text-[11px] text-muted-foreground mt-0.5 leading-[1.45]">
              {name} is mapped to {count} other requirement
              {count !== 1 ? 's' : ''}.
            </p>
          </div>

          <div className="max-h-[320px] overflow-y-auto">
            {live.length > 0 && (
              <div className="pt-[10px] px-2 pb-2">
                <div className="text-[9.5px] font-[700] uppercase tracking-[0.09em] text-muted-foreground px-[6px] mb-1">
                  Still open
                </div>
                {live.map(({ req, meta }) => (
                  <MappingEntry
                    key={req.requirement_id}
                    req={req}
                    meta={meta}
                  />
                ))}
              </div>
            )}
            {done.length > 0 && (
              <div
                className={cn(
                  'pt-[10px] px-2 pb-2',
                  live.length > 0 && 'border-t border-border'
                )}
              >
                <div className="text-[9.5px] font-[700] uppercase tracking-[0.09em] text-muted-foreground px-[6px] mb-1">
                  Closed
                </div>
                {done.map(({ req, meta }) => (
                  <MappingEntry
                    key={req.requirement_id}
                    req={req}
                    meta={meta}
                  />
                ))}
              </div>
            )}
          </div>

          {footerNote}
        </HoverCardContent>
      </HoverCard>
    </>
  );
};

const Panel = ({ children }) => (
  <div
    className="flex flex-col rounded-[10px] border border-border bg-card p-[16px_18px] shadow-[0_1px_2px_rgba(0,0,0,0.03)]"
    style={{ minHeight: 300 }}
  >
    {children}
  </div>
);

const SectionLabel = ({ icon: Icon, children, meta, color }) => (
  <div className="flex items-center justify-between mb-3">
    <span className="flex items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-[0.08em] text-muted-foreground">
      <Icon style={{ width: 12, height: 12, strokeWidth: 2 }} />
      {children}
    </span>
    {meta != null && (
      <span
        className="font-mono text-[10.5px] font-semibold"
        style={{ color: color || 'var(--foreground-strong)' }}
      >
        {meta}
      </span>
    )}
  </div>
);

const CandidateMatchRow = ({
  vm,
  expanded,
  onToggle,
  selected,
  onToggleSelect,
  selectDisabled,
  onProfile,
  showSelection = true,
}) => {
  const [gapsOpen, setGapsOpen] = useState(false);

  const accent = tierColorVar(vm.tier);
  const detailBgCls = DETAIL_BG_CLS[vm.tier] || DETAIL_BG_CLS.explore;
  const score = Math.round(vm.score || 0);
  const rank = String(vm.rank || 1).padStart(2, '0');

  const ctcFlag = (vm.fit_flags || []).find((f) => f.field === 'ctc');
  let ctcTone;
  if (ctcFlag?.passed === false) ctcTone = 'danger';
  else if (ctcFlag?.passed === null) ctcTone = 'warn';

  const skillMap = vm.skill_map || [];
  const visibleSkills = skillMap.filter((s) => s.status !== 'missing');
  const missingSkills = skillMap.filter((s) => s.status === 'missing');

  const coverageTotal =
    (vm.coverage?.matched || 0) +
      (vm.coverage?.partial || 0) +
      (vm.coverage?.missing || 0) || 1;

  return (
    <div
      className={cn(
        'border-b border-border last:border-b-0',
        expanded && detailBgCls
      )}
    >
      {/* Collapsed row */}
      <div
        role="button"
        tabIndex={0}
        className={cn(
          'flex items-stretch cursor-pointer transition-colors',
          !expanded && 'hover:bg-[#F4F8FC] dark:hover:bg-white/[0.04]'
        )}
        onClick={() => onToggle(vm.candidate_id)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle(vm.candidate_id);
          }
        }}
      >
        {/* Tier accent bar */}
        <span className="w-[3px] shrink-0" style={{ background: accent }} />

        <div className="flex-1 min-w-0 flex items-center gap-3.5 py-3.5 pl-4 pr-5">
          {/* Checkbox — CSE browses matches read-only from this page (no
              submit action of their own here), so selection has nothing to
              do. */}
          {showSelection && (
            <div
              role="button"
              tabIndex={-1}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
              className="flex items-center justify-center"
            >
              <Checkbox
                checked={selected}
                onCheckedChange={() => onToggleSelect(vm.candidate_id)}
                disabled={selectDisabled && !selected}
              />
            </div>
          )}

          {/* Rank */}
          <span className="font-mono text-[11px] font-semibold min-w-[22px] text-center text-muted-foreground">
            {rank}
          </span>

          {/* Avatar + status dot */}
          <button
            type="button"
            className="relative shrink-0 cursor-pointer border-none bg-transparent p-0"
            onClick={(e) => {
              e.stopPropagation();
              onProfile(vm.candidate_id);
            }}
            aria-label={`View ${vm.name}'s profile`}
          >
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center text-[12.5px] font-[650]"
              style={{
                background: tintFrom(accent, 18),
                color: accent,
              }}
            >
              {vm.initials}
            </div>
            <span
              className="absolute bottom-0 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-white dark:border-card"
              style={{ background: accent }}
            />
          </button>

          {/* Info */}
          <div className="flex-1 min-w-0">
            {/* Name row */}
            <div className="flex items-center gap-2 mb-0.5 flex-wrap">
              <span className="text-[14px] font-[650] text-foreground-strong tracking-[-0.01em]">
                {vm.name}
              </span>
              <span
                className={cn(
                  'px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-[0.06em]',
                  TIER_BADGE_CLS[vm.tier] || TIER_BADGE_CLS.explore
                )}
                style={
                  vm.tier !== 'explore'
                    ? {
                        background:
                          vm.tier === 'best'
                            ? 'var(--ds-success)'
                            : 'var(--ds-warning)',
                      }
                    : undefined
                }
              >
                {TIERS[vm.tier]?.label || vm.tier}
              </span>
              {vm.other_requirement_mappings && (
                <OtherRequirementsPill
                  name={vm.name}
                  mappings={vm.other_requirement_mappings}
                />
              )}
            </div>

            {/* Job meta */}
            <div className="flex items-center text-[12px] text-muted-foreground">
              <span className="inline-flex items-center gap-1">
                <Briefcase
                  style={{ width: 12, height: 12, strokeWidth: 1.8 }}
                />
                {vm.title}
                {vm.company ? ` at ${vm.company}` : ''}
              </span>
              {vm.location && (
                <>
                  <span className="mx-2 w-[3px] h-[3px] rounded-full inline-block bg-border" />
                  <span className="inline-flex items-center gap-1">
                    <MapPin
                      style={{ width: 12, height: 12, strokeWidth: 1.8 }}
                    />
                    {vm.location}
                  </span>
                </>
              )}
            </div>

            {/* Fit line */}
            {(vm.fit_flags || []).length > 0 && (
              <div className="flex items-center gap-2.5 mt-1.5 flex-wrap max-[900px]:hidden">
                {vm.fit_flags.map((f) => (
                  <FitItem
                    key={f.field}
                    state={fitState(f.passed)}
                    label={f.label}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Right: metrics + score ring + chevron */}
          <div className="flex items-center gap-3.5 shrink-0">
            {/* Metric group */}
            <div
              className="flex bg-card rounded-lg border border-border max-[900px]:hidden"
              style={{ padding: '6px 2px', gap: 0 }}
            >
              <Metric
                value={vm.must_have_matched ?? 0}
                total={vm.must_have_total ?? 0}
                label="Must-Have"
                strong={
                  vm.must_have_matched > 0 &&
                  vm.must_have_matched === vm.must_have_total
                }
              />
              <div
                className="w-px self-stretch bg-border"
                style={{ margin: '3px 0' }}
              />
              <Metric
                value={vm.nice_to_have_matched ?? 0}
                total={vm.nice_to_have_total ?? 0}
                label="Nice-to-Have"
              />
            </div>

            <ScoreRing score={score} color={accent} />

            <span
              className="w-[22px] h-[22px] flex items-center justify-center transition-transform text-muted-foreground"
              style={{ transform: expanded ? 'rotate(180deg)' : 'none' }}
            >
              <ChevronDown style={{ width: 14, height: 14, strokeWidth: 2 }} />
            </span>
          </div>
        </div>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div
          className="pr-6 pb-6 pt-1"
          style={{
            paddingLeft: 38,
            borderLeft: `4px solid ${accent}`,
            borderBottom: '1px solid var(--border)',
          }}
        >
          {/* AI insight */}
          {vm.insight && (
            <div
              className="flex gap-3.5 items-start rounded-[10px] bg-card p-[14px_18px] mb-5 shadow-[0_1px_2px_rgba(0,0,0,0.03)] border"
              style={{ borderColor: tintFrom(accent, 22) }}
            >
              <span
                className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                style={{ background: tintFrom(accent, 18), color: accent }}
              >
                <Sparkles style={{ width: 16, height: 16, strokeWidth: 1.8 }} />
              </span>
              <div className="flex-1">
                <div
                  className="text-[10.5px] font-bold uppercase tracking-[0.06em] mb-1"
                  style={{ color: accent }}
                >
                  AI Match Analysis
                </div>
                <p className="text-[12.5px] text-foreground-strong leading-[1.6] m-0">
                  {vm.insight}
                </p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            {/* Panel 1 — score breakdown + info */}
            <Panel>
              <SectionLabel
                icon={Activity}
                meta={`${score} / 100`}
                color={accent}
              >
                Score Breakdown
              </SectionLabel>
              <div className="flex flex-col gap-[11px] mb-3.5">
                {(vm.score_breakdown || [])
                  // Proficiency isn't wired up yet — hide it entirely until the
                  // backend marks it available, rather than showing a placeholder.
                  .filter((s) => !(s.key === 'proficiency' && !s.available))
                  .map((s) => (
                    <ScoreBar
                      key={s.key}
                      label={s.label}
                      value={s.value}
                      color={accent}
                    />
                  ))}
              </div>
              <div className="h-px -mx-[18px] mb-3.5 bg-border" />
              <div className="flex flex-col gap-2.5">
                {(vm.info_rows || [])
                  // Resume Score is hidden for now — not ready to surface yet.
                  .filter((row) => row.key !== 'Resume Score')
                  .map((row) => (
                    <InfoRow
                      key={row.key}
                      k={row.key}
                      v={row.value ?? '—'}
                      tone={row.key === 'Expected CTC' ? ctcTone : undefined}
                    />
                  ))}
              </div>
            </Panel>

            {/* Panel 2 — skill map + coverage */}
            <Panel>
              <SectionLabel
                icon={Target}
                meta={`${vm.coverage?.matched ?? 0} / ${coverageTotal}`}
                color={accent}
              >
                Skill Map vs JD
              </SectionLabel>

              {/* Coverage bar pinned at top — matches reference after JS restructure */}
              <div className="mb-3.5">
                <div className="flex justify-between items-baseline text-[11px] mb-1.5">
                  <span className="font-medium text-muted-foreground">
                    Overall Coverage
                  </span>
                  <span
                    className="font-mono text-[10.5px] font-[650]"
                    style={{ color: 'var(--foreground-strong)' }}
                  >
                    {vm.coverage?.matched ?? 0} matched
                    {vm.coverage?.partial
                      ? ` · ${vm.coverage.partial} partial`
                      : ''}
                    {vm.coverage?.missing
                      ? ` · ${vm.coverage.missing} gaps`
                      : ''}
                  </span>
                </div>
                <div className="h-[9px] rounded-[5px] overflow-hidden flex bg-border border border-black/[0.03] dark:border-transparent">
                  <span
                    style={{
                      width: `${((vm.coverage?.matched || 0) / coverageTotal) * 100}%`,
                      background: 'var(--ds-success)',
                      transition: 'width .5s',
                    }}
                  />
                  <span
                    style={{
                      width: `${((vm.coverage?.partial || 0) / coverageTotal) * 100}%`,
                      background: 'var(--ds-warning)',
                      transition: 'width .5s',
                    }}
                  />
                </div>
                <div className="flex gap-3.5 text-[10.5px] mt-[7px] text-muted-foreground">
                  <span className="inline-flex items-center gap-1">
                    <span
                      className="w-2 h-2 rounded-[2px] inline-block"
                      style={{ background: 'var(--ds-success)' }}
                    />
                    Matched
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <span
                      className="w-2 h-2 rounded-[2px] inline-block"
                      style={{ background: 'var(--ds-warning)' }}
                    />
                    Partial
                  </span>
                  {vm.coverage?.missing > 0 && (
                    <span className="inline-flex items-center gap-1">
                      <span className="w-2 h-2 rounded-[2px] inline-block bg-border border border-border" />
                      Gaps
                    </span>
                  )}
                </div>
              </div>

              {/* Matched + partial chips */}
              {visibleSkills.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-1.5">
                  {visibleSkills.map((s) => (
                    <SkillTag
                      key={s.name}
                      name={s.name}
                      status={s.status}
                      req={s.required}
                    />
                  ))}
                </div>
              )}

              {/* Collapsible gaps — matches reference restructureSkillPanels() */}
              {missingSkills.length > 0 && (
                <>
                  <button
                    type="button"
                    className="inline-flex items-center gap-1.5 bg-transparent border-none cursor-pointer text-[11.5px] font-semibold py-1 px-0.5 mb-1 transition-colors text-muted-foreground"
                    onClick={(e) => {
                      e.stopPropagation();
                      setGapsOpen((g) => !g);
                    }}
                  >
                    <ChevronRight
                      style={{
                        width: 12,
                        height: 12,
                        strokeWidth: 2.2,
                        transform: gapsOpen ? 'rotate(90deg)' : 'none',
                        transition: 'transform .2s',
                      }}
                    />
                    <span className="inline-block w-1.5 h-1.5 rounded-[2px] bg-muted-foreground/40" />
                    {gapsOpen ? 'Hide' : 'Show'} {missingSkills.length} gap
                    {missingSkills.length !== 1 ? 's' : ''}
                  </button>
                  {gapsOpen && (
                    <div className="flex flex-wrap gap-1.5 pt-0.5">
                      {missingSkills.map((s) => (
                        <SkillTag
                          key={s.name}
                          name={s.name}
                          status="missing"
                          req={s.required}
                        />
                      ))}
                    </div>
                  )}
                </>
              )}

              {!visibleSkills.length && !missingSkills.length && (
                <span className="text-[11.5px] text-muted-foreground">
                  No JD skills defined.
                </span>
              )}
            </Panel>

            {/* Panel 3 — timeline + actions */}
            <Panel>
              <SectionLabel
                icon={TrendingUp}
                meta={vm.total_experience || ''}
                color={accent}
              >
                Career Progression
              </SectionLabel>
              <div
                className="relative pl-4 flex-1 min-h-0 overflow-y-auto"
                style={{ maxHeight: 220 }}
              >
                <span
                  className="absolute top-2 bottom-2 w-px bg-border"
                  style={{ left: 6 }}
                />
                {(vm.timeline || []).map((t) => (
                  <div
                    key={`${t.role || ''}-${t.company || ''}`}
                    className="relative pb-3.5 last:pb-0"
                  >
                    <span
                      className="absolute -left-[13px] top-1 w-2 h-2 rounded-full border-2"
                      style={{
                        background: t.is_current ? accent : 'var(--card)',
                        borderColor: t.is_current ? accent : 'var(--border)',
                        ...(t.is_current && {
                          boxShadow: `0 0 0 3px ${tintFrom(accent, 15)}`,
                        }),
                      }}
                    />
                    <div className="text-[12.5px] font-[650] text-foreground-strong tracking-[-0.01em]">
                      {t.role}
                    </div>
                    <div className="text-[11.5px] text-muted-foreground">
                      {t.company}
                      {t.period ? ` · ${t.period}` : ''}
                    </div>
                  </div>
                ))}
              </div>

              {/* Actions */}
              <div className="mt-4 pt-3.5 border-t border-border flex flex-col gap-1.5">
                {showSelection && (
                  <button
                    type="button"
                    onClick={() => onToggleSelect(vm.candidate_id)}
                    className="inline-flex items-center justify-center gap-1.5 px-3.5 py-2.5 rounded-[7px] text-[12.5px] font-semibold text-white border-none cursor-pointer transition-colors"
                    style={{
                      background: selected
                        ? 'var(--ds-success)'
                        : 'hsl(var(--primary))',
                    }}
                  >
                    <Bookmark
                      style={{ width: 14, height: 14, strokeWidth: 2 }}
                    />
                    {selected ? 'Selected' : 'Select Candidate'}
                  </button>
                )}
                <div className="flex gap-1.5">
                  <button
                    type="button"
                    onClick={() => onProfile(vm.candidate_id)}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 py-2 rounded-[7px] text-[11.5px] font-[550] bg-card border border-input cursor-pointer transition-colors hover:border-primary hover:text-primary hover:bg-brand-bg-subtle text-muted-foreground"
                  >
                    <Eye style={{ width: 12, height: 12, strokeWidth: 1.8 }} />
                    Profile
                  </button>
                  <button
                    type="button"
                    className="flex-1 inline-flex items-center justify-center gap-1.5 py-2 rounded-[7px] text-[11.5px] font-[550] bg-card border border-input cursor-pointer transition-colors hover:border-primary hover:text-primary hover:bg-brand-bg-subtle text-muted-foreground"
                  >
                    <Calendar
                      style={{ width: 12, height: 12, strokeWidth: 1.8 }}
                    />
                    Schedule
                  </button>
                  <button
                    type="button"
                    className="flex-1 inline-flex items-center justify-center gap-1.5 py-2 rounded-[7px] text-[11.5px] font-[550] bg-card border border-input cursor-pointer transition-colors hover:border-primary hover:text-primary hover:bg-brand-bg-subtle text-muted-foreground"
                  >
                    <Send style={{ width: 12, height: 12, strokeWidth: 1.8 }} />
                    Send
                  </button>
                </div>
              </div>
            </Panel>
          </div>
        </div>
      )}
    </div>
  );
};

export default CandidateMatchRow;
